import * as React from 'react';
import styled from 'styled-components';
import {
  DragDropContext,
  Droppable,
  DroppableStateSnapshot,
  DropResult,
} from 'react-beautiful-dnd';
import LessonBuilderContext from '../../LessonBuilderContext';
import Separator from './components/RenderCards/components/Card/components/Separator';
import InputContainer from 'containers/InputContainer';
import Input, {InputVariant} from 'components/Input';
import RenderCards from './components/RenderCards';
import {FolderSelector} from 'components/FolderSelector';
import reorder from 'helpers/reorder';
import {TagBuilder} from 'components/TagBuilder';
import {Folder, Tag} from 'types';

const Row = styled.div`
  margin: 20px 0;
`;

/**
 * This function will render the content of the lesson.
 * @return {React.ReactElement}
 * @constructor
 */
export default function Content(): React.ReactElement {
  const {lesson, setLesson} = React.useContext(LessonBuilderContext);
  const content = lesson?.content || [];

  /**
   * Render folders.
   * @return {JSX.Element}
   */
  function renderFolders(): JSX.Element {
    /**
     * The change event handler.
     */
    function handleChange(folders: Folder[]): void {
      setLesson && setLesson({
        ...lesson,
        folders,
      });
    }

    return (
      <Row>
        <InputContainer label="Папки">
          <FolderSelector
            type="lessons"
            folders={lesson?.folders}
            onChange={handleChange}
            creatable
          />
        </InputContainer>
      </Row>
    );
  }

  /**
   * Render tags.
   * @return {JSX.Element}
   */
  function renderTags(): JSX.Element {
    /**
     * The change event handler.
     */
    function handleChange(tags: Tag[]): void {
      setLesson && setLesson({
        ...lesson,
        tags,
      });
    }

    return (
      <Row>
        <InputContainer label="Теги">
          <TagBuilder
            type="lessons"
            tags={lesson?.tags}
            onChange={handleChange}
          />
        </InputContainer>
      </Row>
    );
  }

  /**
   * Render time.
   * @return {JSX.Element}
   */
  function renderTime(): JSX.Element {
    /**
     * This blur event handler.
     * @param {React.ChangeEvent<HTMLInputElement>} event
     */
    function handleBlur(event: React.FocusEvent<HTMLInputElement>): void {
      setLesson && setLesson({
        ...lesson,
        time: Number(event.target.value),
      });
    }

    return (
      <Row>
        <Input
          type="number"
          label="Время на изучение урока"
          defaultValue={lesson?.time}
          variant={InputVariant.Accent}
          onBlur={handleBlur}
          placeholder="Например, 1 час"
          required
        />
      </Row>
    );
  }

  /**
   * Render drag drop.
   * @return {JSX.Element}
   */
  function renderDragDrop(): JSX.Element {
    /**
     * Render separator.
     * @param {DroppableStateSnapshot} snapshot
     * @return {JSX.Element | undefined}
     */
    function renderSeparator(
        snapshot: DroppableStateSnapshot,
    ): JSX.Element | undefined {
      return !snapshot.isDraggingOver ? (
        <Separator index={content.length} />
      ) : undefined;
    }

    /**
     * This drag end handle event.
     * @param {DropResult} result
     */
    function handleDragEnd(result: DropResult): void {
      if (!result.destination) return;

      const from = result.source;
      const to = result.destination;

      (lesson && setLesson) && setLesson({
        ...lesson,
        content: reorder(content, from.index, to.index),
      });
    }

    return (
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="content">
          {(provided, snapshot) => (
            <div ref={provided.innerRef} {...provided.droppableProps}>
              <RenderCards content={content} />
              {renderSeparator(snapshot)}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    );
  }

  return (
    <React.Fragment>
      {renderFolders()}
      {renderTags()}
      {renderTime()}
      {renderDragDrop()}
    </React.Fragment>
  );
}
